import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faList,
  faEllipsisV
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { supplierApi } from 'api/endpoints/Supplier';
import { getTenantFromToken } from 'utils/routeUtils';
import { formSubmissionToast } from 'helpers/utils';

function CreateSupplier() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const tenant = getTenantFromToken();

  // Form states
  const [supplierName, setSupplierName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [address, setAddress] = useState('');
  const [phoneContact, setPhoneContact] = useState('');
  const [email, setEmail] = useState('');

  // UI/API states
  const [isDirty, setIsDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch existing supplier details in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchSupplierDetails = async () => {
        try {
          const response = await supplierApi.getSupplier(id);
          if (response.data?.success) {
            const sup = response.data.results || {};
            setSupplierName(sup.supplier_name || '');
            setContactPerson(sup.contact_person || '');
            setAddress(sup.address || '');

            let phoneVal = sup.phone_contact || '';
            if (phoneVal.startsWith('+256')) {
              phoneVal = phoneVal.substring(4);
              if (phoneVal.length === 9) {
                phoneVal = '0' + phoneVal;
              }
            }
            setPhoneContact(phoneVal);
            setEmail(sup.email || '');
          }
        } catch (error) {
          console.error('Error fetching supplier details:', error);
          toast.error('Failed to load supplier details');
        }
      };
      fetchSupplierDetails();
    }
  }, [id, isEditMode]);

  const handleFieldChange = (setter, value) => {
    setter(value);
    setIsDirty(true);
  };

  const handleClear = () => {
    setSupplierName('');
    setContactPerson('');
    setAddress('');
    setPhoneContact('');
    setEmail('');
    setIsDirty(true);
  };

  const handleSubmit = async e => {
    if (e) e.preventDefault();

    if (!supplierName.trim()) {
      toast.error('Supplier Name is required');
      return;
    }
    if (!contactPerson.trim()) {
      toast.error('Contact Person is required');
      return;
    }
    if (!address.trim()) {
      toast.error('Address is required');
      return;
    }
    const cleanPhone = phoneContact.trim().replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      toast.error('Phone Contact must be a 10-digit number');
      return;
    }
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }

    const formattedPhone = cleanPhone.startsWith('0')
      ? cleanPhone.substring(1)
      : cleanPhone;
    const payload = {
      supplier_name: supplierName.trim(),
      contact_person: contactPerson.trim(),
      address: address.trim(),
      phone_contact: `+256${formattedPhone}`,
      email: email.trim()
    };

    try {
      setSubmitting(true);
      let response;
      if (isEditMode) {
        response = await supplierApi.updateSupplier(id, payload);
      } else {
        response = await supplierApi.createSupplier(payload);
      }
      formSubmissionToast(response, toast, 'Supplier', isEditMode);
      setIsDirty(false);
      navigate(`/${tenant}/supplier-report`);
    } catch (error) {
      console.error('Error saving supplier:', error);
      formSubmissionToast(error, toast, 'Supplier', isEditMode);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container
      fluid
      className="px-4 py-3"
      style={{ backgroundColor: '#F8F9FC', minHeight: '100vh' }}
    >
      {/* Top Header Card */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Body className="d-flex justify-content-between align-items-center py-2 px-3">
          <div className="d-flex align-items-center">
            <Button
              variant="link"
              onClick={() => navigate(`/${tenant}/supplier-report`)}
              className="text-secondary p-0 me-3 fs-5"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
            <h5 className="mb-0 fw-bold" style={{ color: '#0A2540' }}>
              Supplier Management
            </h5>
          </div>
          <div className="d-flex align-items-center">
            <Button
              variant="outline-primary"
              className="d-flex align-items-center px-3 fw-semi-bold btn-sm me-2"
              style={{ borderRadius: '6px', borderWidth: '1.5px' }}
              onClick={() => navigate(`/${tenant}/supplier-report`)}
            >
              <FontAwesomeIcon icon={faList} className="me-2" />
              View All Suppliers
            </Button>
            <Button variant="link" className="text-secondary p-0 fs-5">
              <FontAwesomeIcon icon={faEllipsisV} />
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Main Title Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2
          className="mb-0 fw-bold"
          style={{ color: '#0A2540', fontSize: '24px' }}
        >
          {isEditMode ? 'Edit Supplier' : 'Register Supplier'}
        </h2>
        <div
          style={{ color: '#6C7A9C', fontSize: '14px' }}
          className="fw-semi-bold"
        >
          Supplier &gt;{' '}
          <span className="text-primary">{isEditMode ? 'Edit' : 'New'}</span>
        </div>
      </div>

      <Form onSubmit={handleSubmit}>
        <Row className="g-4 mb-5 justify-content-center">
          <Col lg={8} md={12}>
            <Card
              className="shadow-sm border-0"
              style={{ borderRadius: '12px' }}
            >
              <Card.Body className="p-4">
                <Form.Group className="mb-4">
                  <Form.Label
                    className="fw-bold text-secondary mb-2"
                    style={{ fontSize: '13px' }}
                  >
                    Supplier Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Acme Corp Industries"
                    value={supplierName}
                    onChange={e =>
                      handleFieldChange(setSupplierName, e.target.value)
                    }
                    required
                    style={{
                      borderRadius: '8px',
                      padding: '10px 14px',
                      border: '1px solid #D8E0F0'
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label
                    className="fw-bold text-secondary mb-2"
                    style={{ fontSize: '13px' }}
                  >
                    Contact Person <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="First and Last Name"
                    value={contactPerson}
                    onChange={e =>
                      handleFieldChange(setContactPerson, e.target.value)
                    }
                    required
                    style={{
                      borderRadius: '8px',
                      padding: '10px 14px',
                      border: '1px solid #D8E0F0'
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label
                    className="fw-bold text-secondary mb-2"
                    style={{ fontSize: '13px' }}
                  >
                    Address <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Street, City, Zip Code, Country"
                    value={address}
                    onChange={e =>
                      handleFieldChange(setAddress, e.target.value)
                    }
                    required
                    style={{
                      borderRadius: '8px',
                      padding: '10px 14px',
                      border: '1px solid #D8E0F0'
                    }}
                  />
                </Form.Group>

                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Phone Contact <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup
                        style={{ borderRadius: '8px', overflow: 'hidden' }}
                      >
                        <InputGroup.Text
                          style={{
                            backgroundColor: '#F8F9FC',
                            border: '1px solid #D8E0F0',
                            color: '#6C7A9C'
                          }}
                        >
                          +256
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="e.g. 0772123456"
                          value={phoneContact}
                          onChange={e => {
                            const val = e.target.value.replace(/\D/g, '');
                            if (val.length <= 10) {
                              handleFieldChange(setPhoneContact, val);
                            }
                          }}
                          required
                          style={{
                            borderLeft: 'none',
                            padding: '10px 14px',
                            border: '1px solid #D8E0F0'
                          }}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Email <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="supplier@example.com"
                        value={email}
                        onChange={e =>
                          handleFieldChange(setEmail, e.target.value)
                        }
                        required
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Footer Bar */}
        <div
          className="fixed-bottom d-flex justify-content-between align-items-center px-4 py-3 bg-white border-top shadow-lg"
          style={{ zIndex: 1020 }}
        >
          <div>
            {isDirty && (
              <span className="small text-danger fw-bold">
                Unsaved changes in &quot;
                {isEditMode ? 'Edit Supplier' : 'Register Supplier'}&quot;
              </span>
            )}
          </div>
          <div>
            <Button
              variant="outline-secondary"
              className="me-3 px-4 fw-semi-bold btn-sm"
              style={{ borderRadius: '6px' }}
              onClick={handleClear}
            >
              Clear
            </Button>
            <Button
              variant="outline-secondary"
              className="me-3 px-4 fw-semi-bold btn-sm"
              style={{ borderRadius: '6px' }}
              onClick={() => navigate(`/${tenant}/supplier-report`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting}
              className="px-4 fw-semi-bold btn-sm"
              style={{
                borderRadius: '6px',
                backgroundColor: '#0048A2',
                borderColor: '#0048A2'
              }}
            >
              {submitting
                ? isEditMode
                  ? 'Updating...'
                  : 'Registering...'
                : isEditMode
                ? 'Update Supplier'
                : 'Register Supplier'}
            </Button>
          </div>
        </div>
      </Form>
    </Container>
  );
}

export default CreateSupplier;
